import { useState } from "react";
import {
  ChevronDown,
  Download,
  Lightbulb,
  Loader2,
  Lock,
  RotateCcw,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUserDetails,
  useConfiguration,
  useRequestTempPresignedUrls,
  usePutPresignedUrls,
} from "@/hooks";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PRIMARY_COLOR,
  HEADING_COLOR,
  GOOD_COLOR,
  BAD_COLOR,
  DEFAULT_OPTIONS,
  PAPER_SIZES,
  TIMEZONES,
  WARNING_GOOD_COLOR,
  WARNING_BAD_COLOR,
} from "@/lib/constants";
import { useGenerateLightingReport } from "@/hooks/use-generate-lighting-report";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  generateLightingReportSchema,
  GenerateLightingReportRequest,
} from "@/api/generate-lighting-report";
import { Dialog } from "@/components/ui/dialog";
import { DialogTrigger } from "@/components/ui/dialog";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDropzone } from "react-dropzone";
import { parseExcelData } from "@/lib/schemas/lighting";
import { toast } from "sonner";
import posthog from "posthog-js";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";

const LightingPage = () => {
  const { data: user } = useUserDetails();
  const { data: configuration } = useConfiguration();
  const { mutateAsync: generateLightingReport, isPending: isLoading } =
    useGenerateLightingReport();
  const { mutateAsync: requestTempPresignedUrls } =
    useRequestTempPresignedUrls();
  const { mutateAsync: putPresignedUrl } = usePutPresignedUrls();

  const [processing, setProcessing] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    maxFiles: 1,
    multiple: false,
    onDrop: async (acceptedFiles) => {
      setProcessing(true);

      const getKey = async () => {
        const [{ url, key }] = await requestTempPresignedUrls({ amount: 1 });
        await putPresignedUrl({ url, file: acceptedFiles[0] });
        return key;
      };

      const [data, key] = await Promise.all([
        parseExcelData(acceptedFiles[0]),
        getKey(),
      ]);

      if (data.err) {
        console.error(data.val);
        const errors = data.val.map((e) => ({
          errors: e.zodError.error.issues,
          sheetName: e.sheetName,
          rowNumber: e.rowNumber,
        }));
        errors.forEach((e) => {
          e.errors.forEach((error) => {
            toast.error(
              `En la hoja ${e.sheetName} hay un error en la fila ${e.rowNumber}`,
              {
                description: error.message,
                duration: 1000 * 30,
              }
            );
          });
        });
      } else {
        form.setValue("excelData", data.val);
        form.setValue("tmpFileKey", key);
        form.setValue("inputFilename", acceptedFiles[0].name);
      }
      setProcessing(false);
    },
  });

  // todo: find a better way to fill in form with async values
  const form = useForm<GenerateLightingReportRequest>({
    resolver: zodResolver(generateLightingReportSchema),
    values: {
      wallReflectionLimit:
        configuration?.lWallReflectionLimit ??
        Number(DEFAULT_OPTIONS.LIGHTING.WALL_REFLECTION_LIMIT),
      workPlaneReflectionLimit:
        configuration?.lWorkPlaneReflectionLimit ??
        Number(DEFAULT_OPTIONS.LIGHTING.WORK_PLANE_REFLECTION_LIMIT),
      distanceRounding:
        configuration?.lLengthRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.DISTANCE_ROUNDING),
      icRounding:
        configuration?.lIcRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.IC_ROUNDING),
      luxRounding:
        configuration?.lLuxRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.LUX_ROUNDING),
      reflectionRounding:
        configuration?.lReflectionRounding ??
        Number(DEFAULT_OPTIONS.LIGHTING.REFLECTION_ROUNDING),
      primaryColor: configuration?.primaryColor ?? PRIMARY_COLOR,
      headingColor: configuration?.headingColor ?? HEADING_COLOR,
      goodColor: configuration?.goodColor ?? GOOD_COLOR,
      warningGoodColor: configuration?.warningGoodColor ?? WARNING_GOOD_COLOR,
      warningBadColor: configuration?.warningBadColor ?? WARNING_BAD_COLOR,
      badColor: configuration?.badColor ?? BAD_COLOR,
      paperSize: configuration?.paperSize ?? DEFAULT_OPTIONS.GENERAL.PAPER_SIZE,
      marginInches:
        configuration?.marginIn ??
        Number(DEFAULT_OPTIONS.GENERAL.MARGIN_INCHES),
      timezone: configuration?.timezone ?? DEFAULT_OPTIONS.GENERAL.TIMEZONE,
      coverImage:
        configuration?.coverImage ?? DEFAULT_OPTIONS.GENERAL.COVER_IMAGE,
      password: "",
      optionalId: "",
      reportTier: "free",
      // @ts-expect-error: excelData is not required
      excelData: undefined,
      // @ts-expect-error: inputFilename is not required
      inputFilename: undefined,
      // @ts-expect-error: tmpFileKey is not required
      tmpFileKey: undefined,
      outline: configuration?.outline ?? DEFAULT_OPTIONS.GENERAL.OUTLINE,
      sectionDividers:
        configuration?.sectionDividers ??
        DEFAULT_OPTIONS.GENERAL.SECTION_DIVIDERS,
    },
  });

  const [downloadFileType, setDownloadFileType] = useState("xlsx");

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-end gap-6">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Iluminación</h1>
          <p className="text-xs text-muted-foreground">
            Llene la plantilla y suba el archivo para generar un reporte de
            iluminación
          </p>
        </div>
        <div className="flex gap-[2px]">
          {downloadFileType === "xlsx" ? (
            <a
              href="/excel-templates/lighting/Bernardino_Plantilla_Iluminacion.xlsx"
              download
            >
              <Button variant="success" className="rounded-r-none w-36">
                <Download /> Plantilla (.xlsx)
              </Button>{" "}
            </a>
          ) : (
            <a
              href="/excel-templates/lighting/Bernardino_Plantilla_Iluminacion_EXCEL97.xls"
              download
            >
              <Button variant="success" className="rounded-r-none w-36">
                <Download /> Plantilla (.xls)
              </Button>{" "}
            </a>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="success" className="rounded-l-none" size="icon">
                <ChevronDown />{" "}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 font-mono">
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => setDownloadFileType("xlsx")}
                  data-state={
                    downloadFileType === "xlsx" ? "active" : "inactive"
                  }
                  className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none data-[state=active]:border-l-2 border-success"
                >
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDownloadFileType("xls")}
                  data-state={
                    downloadFileType === "xls" ? "active" : "inactive"
                  }
                  className="hover:bg-muted/5 focus:bg-muted/5 text-foreground rounded-none data-[state=active]:border-l-2 border-success"
                >
                  Excel97-2003 (.xls)
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Separator />
      <div className="grid gap-1">
        <Form {...form}>
          <form
            id="lighting-form"
            onSubmit={form.handleSubmit(async (values) => {
              if (values.reportTier === "free") {
                posthog.capture("generate_lighting_free");
              } else {
                posthog.capture("generate_lighting_paid");
              }

              console.log(values);
              const url = await generateLightingReport(values);
              window.open(url, "_blank");
            })}
            className="space-y-6"
          >
            <div className="grid gap-2">
              <div className="flex gap-1 flex-wrap">
                <div className="flex gap-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-r-none rounded-l-md border-1 border-r-0"
                      >
                        Constantes
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="wallReflectionLimit"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Limite de reflexión pared
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="workPlaneReflectionLimit"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Limite de reflexión plano de trabajo
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-none border-y-1"
                      >
                        Decimales
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="distanceRounding"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Presición de distancia
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="icRounding"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Presición de IC
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="luxRounding"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Presición de luxes
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="reflectionRounding"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Presición de porcentaje de reflexión
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-none border-y-1"
                      >
                        Colores
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="primaryColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Primario
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="headingColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Encabezado
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="goodColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Aceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="warningGoodColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Alerta aceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="warningBadColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Alerta inaceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="badColor"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  type="color"
                                  className="hidden"
                                  {...field}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-medium">
                                <span
                                  className="rounded-full size-6"
                                  style={{ backgroundColor: field.value }}
                                ></span>
                                Inaceptable
                              </FormLabel>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="hover:bg-muted/5 focus:bg-muted/5 rounded-l-none rounded-r-md border-1 border-l-0"
                      >
                        Tamaños
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="text-foreground">
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="paperSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Tipo de hoja
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Tamaño" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {PAPER_SIZES.map((size) => (
                                    <SelectItem
                                      key={size.value}
                                      value={size.value}
                                    >
                                      {size.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="marginInches"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                Margen
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step={0.1}
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <FormField
                  control={form.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Zona horaria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIMEZONES.map((timezone) => (
                            <SelectItem
                              key={timezone.value}
                              value={timezone.value}
                            >
                              {timezone.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="hover:bg-muted/5 focus:bg-muted/5 rounded-md border-1 text-foreground"
                      size="icon"
                    >
                      <Lock />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="text-foreground">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium">
                              Contraseña
                            </FormLabel>
                            <FormControl>
                              <Input type="text" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <FormLabel className="text-xs font-medium">Opciones</FormLabel>
                <div className="grid gap-1">
                  <FormField
                    control={form.control}
                    name="coverImage"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir imagen de portada
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="outline"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir tabla de contenido
                        </FormLabel>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sectionDividers"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={!!field.value}
                            onCheckedChange={(e) => field.onChange(e)}
                            className="size-4"
                          />
                        </FormControl>
                        <FormLabel className="text-xs font-medium cursor-pointer">
                          Incluir portadas de secciones
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <FormField
              control={form.control}
              name="excelData"
              render={() => (
                <FormItem>
                  <FormControl>
                    {!form.watch("excelData") ? (
                      <div
                        {...getRootProps()}
                        className="w-full h-[250px] border-2 border-dashed rounded-md bg-accent/10 border-accent cursor-pointer"
                      >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center justify-center h-full text-center w-full">
                          {processing ? (
                            <>
                              <div className="flex items-center gap-2 mb-6">
                                <Loader2 className="size-10 text-primary/60 animate-spin" />
                              </div>
                              <p className="text-sm font-medium text-foreground/80">
                                Se están procesando los datos del archivo Excel
                              </p>
                              <p className="text-xs text-muted-foreground/80">
                                Esto puede tardar unos segundos
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 mb-6">
                                <Lightbulb className="size-10 text-primary/60" />
                                <p className="text-4xl font-bold text-primary/60">
                                  Iluminación
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Upload className="size-4 text-foreground/80" />
                                <p className="text-sm font-medium text-foreground/80">
                                  {isDragActive
                                    ? "Suelte el archivo aquí"
                                    : "Arrastre el archivo aquí o haga clic para seleccionar"}
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground/80">
                                Máximo 1 archivo (xlsx/xls)
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full border rounded bg-accent/5 p-4">
                        <Tabs defaultValue="administrative" className="w-full">
                          <TabsList className="grid w-full grid-cols-5">
                            <TabsTrigger value="administrative">
                              Datos Admin
                            </TabsTrigger>
                            <TabsTrigger value="evaluations">
                              Evaluaciones
                            </TabsTrigger>
                            <TabsTrigger value="recognitions">
                              Reconocimientos
                            </TabsTrigger>
                            <TabsTrigger value="equipment">Equipos</TabsTrigger>
                            <TabsTrigger value="exposure">
                              Grupos Exp.
                            </TabsTrigger>
                          </TabsList>

                          <TabsContent
                            value="administrative"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.administrativeData ? (
                              <div className="space-y-3">
                                {Object.entries(
                                  form.getValues("excelData")
                                    ?.administrativeData || {}
                                ).map(([key, value]) => (
                                  <div
                                    key={key}
                                    className="flex flex-col space-y-1"
                                  >
                                    <label className="text-sm font-medium capitalize text-foreground">
                                      {key.replace(/([A-Z])/g, " $1").trim()}
                                    </label>
                                    <span className="text-sm text-muted-foreground">
                                      {value || "No proporcionado"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay datos administrativos disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="evaluations"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.evaluationRows &&
                            form.getValues("excelData")!.evaluationRows.length >
                              0 ? (
                              <div className="grid gap-1">
                                <div className="flex items-center gap-1">
                                  <h6 className="text-xs font-medium">
                                    Fechas:
                                  </h6>
                                  {Array.from(
                                    new Set(
                                      form
                                        .getValues("excelData")!
                                        .evaluationRows.map(
                                          (evaluation) =>
                                            evaluation["fecha"] ?? ""
                                        )
                                        .filter(Boolean)
                                    )
                                  ).map((date) => (
                                    <Badge
                                      key={date}
                                      className="text-[10px] px-2 py-0.5"
                                    >
                                      {date}
                                    </Badge>
                                  ))}
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Puesto
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Identificación
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Ubicacion
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .evaluationRows.map(
                                        (evaluation, index) => (
                                          <TableRow
                                            key={index}
                                            className="hover:bg-accent/5"
                                          >
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["departamento"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(evaluation["area"] ?? "")}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["puesto"] ?? ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["identificacion"] ??
                                                  ""
                                              )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {String(
                                                evaluation["ubicacion"] ?? ""
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .evaluationRows.length
                                        }{" "}
                                        evaluaciones
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay evaluaciones disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="recognitions"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.recognitionRows &&
                            form.getValues("excelData")!.recognitionRows
                              .length > 0 ? (
                              <div className="grid gap-1">
                                <div className="flex items-center gap-1">
                                  <h6 className="text-xs font-medium">
                                    Fechas:
                                  </h6>
                                  {Array.from(
                                    new Set(
                                      form
                                        .getValues("excelData")!
                                        .recognitionRows.map(
                                          (recognition) =>
                                            recognition["fecha"] ?? ""
                                        )
                                        .filter(Boolean)
                                    )
                                  ).map((date) => (
                                    <Badge
                                      key={date}
                                      className="text-[10px] px-2 py-0.5"
                                    >
                                      {date}
                                    </Badge>
                                  ))}
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Criterio
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Tipo de Luminarias
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .recognitionRows.map(
                                        (recognition, index) => (
                                          <TableRow
                                            key={index}
                                            className="hover:bg-accent/5"
                                          >
                                            <TableCell className="text-xs">
                                              {recognition["departamento"] ??
                                                ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition["area"] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {recognition[
                                                "criterio de la evaluacion"
                                              ] ?? ""}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                              {(() => {
                                                const tipoLuminarias = [
                                                  1, 2, 3, 4, 5,
                                                ]
                                                  .filter(
                                                    (i) =>// @ts-expect-error - This is a workaround to get the type of the data
                                                      recognition?.[
                                                        `cantidad luminarias (${i})`
                                                      ] && // @ts-expect-error - This is a workaround to get the type of the data
                                                      recognition?.[
                                                        `tipo de luminarias (${i})`
                                                      ]
                                                  )
                                                  .map(
                                                    (i) =>
                                                      `${
                                                        // @ts-expect-error - This is a workaround to get the type of the data
                                                        recognition?.[
                                                          `cantidad luminarias (${i})`
                                                        ]
                                                      } x ${
                                                        // @ts-expect-error - This is a workaround to get the type of the data
                                                        recognition?.[
                                                          `tipo de luminarias (${i})`
                                                        ]
                                                      }`
                                                  );
                                                return tipoLuminarias
                                                  .filter(Boolean)
                                                  .map((item) => (
                                                    <Badge
                                                      key={item}
                                                      variant="secondary"
                                                      className="text-[8px] px-1 py-0 h-auto mr-1"
                                                    >
                                                      {item}
                                                    </Badge>
                                                  ));
                                              })()}
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={4}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .recognitionRows.length
                                        }{" "}
                                        reconocimientos
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay reconocimientos disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="equipment"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.equipmentRows &&
                            form.getValues("excelData")!.equipmentRows.length >
                              0 ? (
                              <div className="space-y-4">
                                {form
                                  .getValues("excelData")!
                                  .equipmentRows.map((item, index) => {
                                    // Find corrections for this equipment based on serial number
                                    const itemSerial = item["equipo no. serie"];
                                    const equipmentCorrections =
                                      form
                                        .getValues("excelData")
                                        ?.correctionRows?.filter(
                                          (correction) =>
                                            correction["equipo no. serie"] ===
                                            itemSerial
                                        ) || [];

                                    return (
                                      <div
                                        key={index}
                                        className="border rounded p-3 bg-background/50"
                                      >
                                        <div>
                                          <div className="flex items-baseline gap-2">
                                            <h4 className="text-lg font-medium">
                                              {item["equipo no. serie"]}
                                            </h4>
                                            <div className="flex gap-1">
                                              {item["equipo marca"] && (
                                                <Badge
                                                  variant="secondary"
                                                  className="text-[8px] px-1 py-0 h-auto"
                                                >
                                                  {item["equipo marca"]}
                                                </Badge>
                                              )}
                                              {item["equipo modelo"] && (
                                                <Badge className="text-[8px] px-1 py-0 h-auto">
                                                  {item["equipo modelo"]}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3 mt-2">
                                            <span className="text-xs text-muted-foreground">
                                              Calibraciónes:
                                            </span>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                              <span>
                                                {Number(
                                                  item["calibracion inicial"]
                                                ).toFixed(2) ?? "N/A"}
                                              </span>
                                              <svg
                                                className="text-primary animate-pulse transition-all duration-1000 hover:scale-110"
                                                width="24"
                                                height="12"
                                                viewBox="0 0 24 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M1 6H23M23 6L18 1M23 6L18 11"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                              <span>
                                                {Number(
                                                  item["calibracion final"]
                                                ).toFixed(2) ?? "N/A"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {equipmentCorrections.length > 0 && (
                                          <div className="mt-3 pt-3 border-t">
                                            <h5 className="text-xs font-medium mb-3">
                                              Correcciones
                                            </h5>
                                            <div className="h-32 w-full">
                                              <ChartContainer
                                                config={{}}
                                                className="h-full w-full"
                                              >
                                                <LineChart
                                                  data={(() => {
                                                    // First, determine the overall lux range across all segments
                                                    let minOverallLux =
                                                      Infinity;
                                                    let maxOverallLux =
                                                      -Infinity;

                                                    equipmentCorrections.forEach(
                                                      (correction) => {
                                                        const minLux =
                                                          Number(
                                                            correction[
                                                              "minimo (lux)"
                                                            ]
                                                          ) || 0;
                                                        const maxLux =
                                                          Number(
                                                            correction[
                                                              "maximo (lux)"
                                                            ]
                                                          ) || minLux + 100;
                                                        minOverallLux =
                                                          Math.min(
                                                            minOverallLux,
                                                            minLux
                                                          );
                                                        maxOverallLux =
                                                          Math.max(
                                                            maxOverallLux,
                                                            maxLux
                                                          );
                                                      }
                                                    );

                                                    // Create a comprehensive set of X-axis points
                                                    const allLuxValues =
                                                      new Set<number>();
                                                    equipmentCorrections.forEach(
                                                      (correction) => {
                                                        const minLux =
                                                          Number(
                                                            correction[
                                                              "minimo (lux)"
                                                            ]
                                                          ) || 0;
                                                        const maxLux =
                                                          Number(
                                                            correction[
                                                              "maximo (lux)"
                                                            ]
                                                          ) || minLux + 100;
                                                        const steps = 5;
                                                        for (
                                                          let i = 0;
                                                          i <= steps;
                                                          i++
                                                        ) {
                                                          const luxValue =
                                                            Math.round(
                                                              minLux +
                                                                (maxLux -
                                                                  minLux) *
                                                                  (i / steps)
                                                            );
                                                          allLuxValues.add(
                                                            luxValue
                                                          );
                                                        }
                                                      }
                                                    );

                                                    // Convert to sorted array
                                                    const sortedLuxValues =
                                                      Array.from(
                                                        allLuxValues
                                                      ).sort((a, b) => a - b);

                                                    // Create data points for each lux value
                                                    const allPoints: Array<{
                                                      lux: number;
                                                      original: number;
                                                      corrected: number;
                                                      segment: string;
                                                      [key: string]:
                                                        | number
                                                        | string
                                                        | undefined;
                                                    }> = [];

                                                    sortedLuxValues.forEach(
                                                      (luxValue) => {
                                                        const point: {
                                                          lux: number;
                                                          original: number;
                                                          corrected: number;
                                                          segment: string;
                                                          [key: string]:
                                                            | number
                                                            | string
                                                            | undefined;
                                                        } = {
                                                          lux: luxValue,
                                                          original: luxValue, // Always show original as y = x
                                                          corrected: luxValue,
                                                          segment: "All",
                                                        };

                                                        // Add segment-specific corrected data only if this lux value falls within the segment's range
                                                        equipmentCorrections.forEach(
                                                          (
                                                            correction,
                                                            corrIndex
                                                          ) => {
                                                            const minLux =
                                                              Number(
                                                                correction[
                                                                  "minimo (lux)"
                                                                ]
                                                              ) || 0;
                                                            const maxLux =
                                                              Number(
                                                                correction[
                                                                  "maximo (lux)"
                                                                ]
                                                              ) || minLux + 100;
                                                            const aditiva =
                                                              Number(
                                                                correction[
                                                                  "correccion aditiva"
                                                                ]
                                                              ) || 0;
                                                            const multiplicativa =
                                                              Number(
                                                                correction[
                                                                  "correccion multiplicativa"
                                                                ]
                                                              ) || 1;

                                                            if (
                                                              luxValue >=
                                                                minLux &&
                                                              luxValue <= maxLux
                                                            ) {
                                                              const correctedValue =
                                                                luxValue *
                                                                  multiplicativa +
                                                                aditiva;
                                                              point[
                                                                `correc. ${corrIndex}`
                                                              ] =
                                                                correctedValue;
                                                            } else {
                                                              point[
                                                                `correc. ${corrIndex}`
                                                              ] = undefined;
                                                            }
                                                          }
                                                        );

                                                        allPoints.push(point);
                                                      }
                                                    );

                                                    return allPoints;
                                                  })()}
                                                  margin={{
                                                    top: 5,
                                                    right: 5,
                                                    left: 0,
                                                    bottom: 5,
                                                  }}
                                                >
                                                  <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    stroke="#e5e7eb"
                                                  />
                                                  <XAxis
                                                    dataKey="lux"
                                                    tick={{ fontSize: 8 }}
                                                    axisLine={true}
                                                    tickLine={true}
                                                    stroke="#6b7280"
                                                    scale="linear"
                                                  />
                                                  <YAxis
                                                    tick={{ fontSize: 8 }}
                                                    axisLine={true}
                                                    tickLine={true}
                                                    stroke="#6b7280"
                                                    width={30}
                                                    scale="linear"
                                                  />
                                                  <ChartTooltip
                                                    content={
                                                      <ChartTooltipContent />
                                                    }
                                                  />
                                                  {/* Single continuous original line (y = x) */}
                                                  <Line
                                                    dataKey="original"
                                                    stroke="var(--chart-2)"
                                                    strokeWidth={1}
                                                    strokeDasharray="3 3"
                                                    dot={false}
                                                    activeDot={{
                                                      r: 3,
                                                      fill: "var(--chart-2)",
                                                    }}
                                                  />
                                                  {/* Segmented corrected lines */}
                                                  {equipmentCorrections.map(
                                                    (_, corrIndex) => (
                                                      <Line
                                                        key={`correc.${corrIndex}`}
                                                        dataKey={`correc. ${corrIndex}`}
                                                        stroke="var(--chart-1)"
                                                        strokeWidth={1.5}
                                                        dot={false}
                                                        activeDot={{
                                                          r: 4,
                                                          fill: "var(--chart-1)",
                                                        }}
                                                        connectNulls={false}
                                                      />
                                                    )
                                                  )}
                                                </LineChart>
                                              </ChartContainer>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay equipos disponibles
                              </p>
                            )}
                          </TabsContent>

                          <TabsContent
                            value="exposure"
                            className="mt-4 max-h-[500px] overflow-y-auto"
                          >
                            {form.getValues("excelData")?.exposureGroupRows &&
                            form.getValues("excelData")!.exposureGroupRows
                              .length > 0 ? (
                              <div>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="hover:bg-accent/5">
                                      <TableHead className="text-xs">
                                        Departamento
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Area
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Puesto
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        POE
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {form
                                      .getValues("excelData")!
                                      .exposureGroupRows.map((group, index) => (
                                        <TableRow
                                          key={index}
                                          className="hover:bg-accent/5"
                                        >
                                          <TableCell className="text-xs">
                                            {String(
                                              group["departamento"] ?? ""
                                            )}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {String(group["area"] ?? "")}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {String(group["puesto"] ?? "")}
                                          </TableCell>
                                          <TableCell className="text-xs">
                                            {String(group["poe"] ?? "")}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                  <TableFooter>
                                    <TableRow>
                                      <TableCell
                                        colSpan={4}
                                        className="text-center text-xs text-muted-foreground"
                                      >
                                        Total:{" "}
                                        {
                                          form.getValues("excelData")!
                                            .exposureGroupRows.length
                                        }{" "}
                                        grupos de exposición
                                      </TableCell>
                                    </TableRow>
                                  </TableFooter>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">
                                No hay grupos de exposición disponibles
                              </p>
                            )}
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-between">
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant="destructive"
                        type="button"
                        disabled={isLoading || !form.watch("excelData")}
                        className="gap-2"
                        onClick={() => form.reset()}
                      >
                        <RotateCcw className="size-4" />
                        Resetear
                      </Button>
                    </TooltipTrigger>
                    {!form.watch("excelData") && (
                      <TooltipContent>
                        <p>
                          Selecciona al menos un archivo para resetear
                          formulario
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        variant="secondary"
                        type="submit"
                        disabled={isLoading || !form.watch("excelData")}
                        className="w-32 gap-2"
                        onClick={() => form.setValue("reportTier", "free")}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Procesando...
                          </>
                        ) : (
                          "Preliminar"
                        )}
                      </Button>
                    </TooltipTrigger>
                    {!form.watch("excelData") && (
                      <TooltipContent>
                        <p>
                          Selecciona al menos un archivo para generar reporte
                          preliminar
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            disabled={
                              isLoading ||
                              !form.watch("excelData") ||
                              user?.type !== "subscribed"
                            }
                            className="w-32 gap-2"
                          >
                            Final
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Id de Proyecto</DialogTitle>
                            <DialogDescription>
                              Puede ingresar un identificador para el proyecto.
                              Este sera incluido en el reporte.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4">
                            <FormField
                              control={form.control}
                              name="optionalId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-sm font-medium">
                                    Identificador
                                  </FormLabel>
                                  <FormControl>
                                    <Input type="text" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button
                                variant="outline"
                                type="button"
                                disabled={isLoading}
                                className="w-32 gap-2 border-destructive text-destructive focus:bg-transparent hover:bg-transparent bg-transparent hover:text-destructive focus:text-destructive"
                              >
                                Cancelar
                              </Button>
                            </DialogClose>
                            <Button
                              form="lighting-form"
                              type="submit"
                              disabled={isLoading || !form.watch("excelData")}
                              className="w-32 gap-2"
                              onClick={() => {
                                form.setValue("reportTier", "paid");
                              }}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="size-4 animate-spin" />
                                  Procesando...
                                </>
                              ) : (
                                "Final"
                              )}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </TooltipTrigger>
                    {(!form.watch("excelData") ||
                      user?.type !== "subscribed") && (
                      <TooltipContent>
                        {user?.type !== "subscribed" ? (
                          <p>Solo disponible para usuarios con suscripción</p>
                        ) : (
                          <p>
                            Selecciona al menos un archivo para generar reporte
                            final
                          </p>
                        )}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default LightingPage;
